import {
  createEvent as publicCreateEvent,
  cloneEvent as publicCloneEvent,
  updateEvent as publicUpdateEvent,
  publishDraftEvent as publicPublishDraftEvent,
  cancelEvent as publicCancelEvent,
  bulkCancelEventsByFilter as publicBulkCancelEventsByFilter,
  deleteEvent as publicDeleteEvent,
  bulkDeleteEventsByFilter as publicBulkDeleteEventsByFilter,
  queryEvents as publicQueryEvents,
  typedQueryEvents as publicTypedQueryEvents,
  countEventsByStatus as publicCountEventsByStatus,
  listEventsByCategory as publicListEventsByCategory,
  getEvent as publicGetEvent,
  getEventBySlug as publicGetEventBySlug,
} from './events-v3-event-wix-events-v-2.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  EventQuery,
  EventsQueryBuilder,
  QueryEventsOptions,
  typedQueryEvents as universalTypedQueryEvents,
} from './events-v3-event-wix-events-v-2.universal.js';
import { onEventCanceled as publicOnEventCanceled } from './events-v3-event-wix-events-v-2.public.js';
import { onEventCloned as publicOnEventCloned } from './events-v3-event-wix-events-v-2.public.js';
import { onEventCreated as publicOnEventCreated } from './events-v3-event-wix-events-v-2.public.js';
import { onEventDeleted as publicOnEventDeleted } from './events-v3-event-wix-events-v-2.public.js';
import { onEventEnded as publicOnEventEnded } from './events-v3-event-wix-events-v-2.public.js';
import { onEventPublished as publicOnEventPublished } from './events-v3-event-wix-events-v-2.public.js';
import { onEventReminder as publicOnEventReminder } from './events-v3-event-wix-events-v-2.public.js';
import { onEventStarted as publicOnEventStarted } from './events-v3-event-wix-events-v-2.public.js';
import { onEventUpdated as publicOnEventUpdated } from './events-v3-event-wix-events-v-2.public.js';

function customQueryEvents(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryEventsOptions) =>
      publicQueryEvents(httpClient)(options),
    typedQueryFunction: (query: EventQuery, options?: QueryEventsOptions) =>
      publicTypedQueryEvents(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: EventQuery,
    options?: QueryEventsOptions
  ): ReturnType<typeof universalTypedQueryEvents>;
  function overloadedQuery(options?: QueryEventsOptions): EventsQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: EventQuery | QueryEventsOptions,
    options?: QueryEventsOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createEvent: MaybeContext<
  BuildRESTFunction<typeof publicCreateEvent> & typeof publicCreateEvent
> = /*#__PURE__*/ createRESTModule(publicCreateEvent);
export const cloneEvent: MaybeContext<
  BuildRESTFunction<typeof publicCloneEvent> & typeof publicCloneEvent
> = /*#__PURE__*/ createRESTModule(publicCloneEvent);
export const updateEvent: MaybeContext<
  BuildRESTFunction<typeof publicUpdateEvent> & typeof publicUpdateEvent
> = /*#__PURE__*/ createRESTModule(publicUpdateEvent);
export const publishDraftEvent: MaybeContext<
  BuildRESTFunction<typeof publicPublishDraftEvent> &
    typeof publicPublishDraftEvent
> = /*#__PURE__*/ createRESTModule(publicPublishDraftEvent);
export const cancelEvent: MaybeContext<
  BuildRESTFunction<typeof publicCancelEvent> & typeof publicCancelEvent
> = /*#__PURE__*/ createRESTModule(publicCancelEvent);
export const bulkCancelEventsByFilter: MaybeContext<
  BuildRESTFunction<typeof publicBulkCancelEventsByFilter> &
    typeof publicBulkCancelEventsByFilter
> = /*#__PURE__*/ createRESTModule(publicBulkCancelEventsByFilter);
export const deleteEvent: MaybeContext<
  BuildRESTFunction<typeof publicDeleteEvent> & typeof publicDeleteEvent
> = /*#__PURE__*/ createRESTModule(publicDeleteEvent);
export const bulkDeleteEventsByFilter: MaybeContext<
  BuildRESTFunction<typeof publicBulkDeleteEventsByFilter> &
    typeof publicBulkDeleteEventsByFilter
> = /*#__PURE__*/ createRESTModule(publicBulkDeleteEventsByFilter);
export const countEventsByStatus: MaybeContext<
  BuildRESTFunction<typeof publicCountEventsByStatus> &
    typeof publicCountEventsByStatus
> = /*#__PURE__*/ createRESTModule(publicCountEventsByStatus);
export const listEventsByCategory: MaybeContext<
  BuildRESTFunction<typeof publicListEventsByCategory> &
    typeof publicListEventsByCategory
> = /*#__PURE__*/ createRESTModule(publicListEventsByCategory);
export const getEvent: MaybeContext<
  BuildRESTFunction<typeof publicGetEvent> & typeof publicGetEvent
> = /*#__PURE__*/ createRESTModule(publicGetEvent);
export const getEventBySlug: MaybeContext<
  BuildRESTFunction<typeof publicGetEventBySlug> & typeof publicGetEventBySlug
> = /*#__PURE__*/ createRESTModule(publicGetEventBySlug);
export const queryEvents: MaybeContext<
  BuildRESTFunction<typeof customQueryEvents> & typeof customQueryEvents
> = /*#__PURE__*/ createRESTModule(customQueryEvents);
/**
 * Triggered when an event is canceled.
 */
export const onEventCanceled: BuildEventDefinition<
  typeof publicOnEventCanceled
> &
  typeof publicOnEventCanceled = createEventModule(publicOnEventCanceled);
/**
 * Triggered when an event is cloned.
 */
export const onEventCloned: BuildEventDefinition<typeof publicOnEventCloned> &
  typeof publicOnEventCloned = createEventModule(publicOnEventCloned);
/**
 * Triggered when an event is created.
 */
export const onEventCreated: BuildEventDefinition<typeof publicOnEventCreated> &
  typeof publicOnEventCreated = createEventModule(publicOnEventCreated);
/**
 * Triggered when an event is deleted.
 */
export const onEventDeleted: BuildEventDefinition<typeof publicOnEventDeleted> &
  typeof publicOnEventDeleted = createEventModule(publicOnEventDeleted);
/**
 * Triggered when an event has ended.
 */
export const onEventEnded: BuildEventDefinition<typeof publicOnEventEnded> &
  typeof publicOnEventEnded = createEventModule(publicOnEventEnded);
/**
 * Triggered when an event is published.
 */
export const onEventPublished: BuildEventDefinition<
  typeof publicOnEventPublished
> &
  typeof publicOnEventPublished = createEventModule(publicOnEventPublished);
/**
 * Triggered when a certain amount of time is left until the event. In total there are 6 reminders:
 * - 7 days
 * - 3 days
 * - 1 day
 * - 2 hours
 * - 1 hour
 * - 30 minutes
 */
export const onEventReminder: BuildEventDefinition<
  typeof publicOnEventReminder
> &
  typeof publicOnEventReminder = createEventModule(publicOnEventReminder);
/**
 * Triggered when an event has started.
 */
export const onEventStarted: BuildEventDefinition<typeof publicOnEventStarted> &
  typeof publicOnEventStarted = createEventModule(publicOnEventStarted);
/**
 * Triggered when an event is updated.
 */
export const onEventUpdated: BuildEventDefinition<typeof publicOnEventUpdated> &
  typeof publicOnEventUpdated = createEventModule(publicOnEventUpdated);

export {
  LocationType,
  SubdivisionType,
  RecurrenceStatusStatus,
  Status,
  RegistrationTypeType,
  RegistrationStatusStatus,
  ResponseType,
  TaxType,
  CheckoutType,
  GuestTypeType,
  InitialRegistrationTypeType,
  InputControlType,
  ValueType,
  ConferenceTypeType,
  Type,
  NodeType,
  BackgroundType,
  GradientType,
  WidthType,
  PluginContainerDataAlignment,
  ButtonDataType,
  Target,
  TextAlignment,
  LineStyle,
  Width,
  DividerDataAlignment,
  ViewMode,
  LayoutType,
  Orientation,
  Crop,
  ThumbnailsAlignment,
  GIFType,
  Source,
  StylesPosition,
  MapType,
  ViewRole,
  VoteRole,
  PollLayoutType,
  PollLayoutDirection,
  PollDesignBackgroundType,
  DecorationType,
  FontType,
  Position,
  AspectRatio,
  Resizing,
  Placement,
  CardStylesType,
  Alignment,
  Layout,
  AppType,
  InitialExpandedItems,
  Direction,
  VerticalAlignment,
  NullValue,
  ImageScalingScaling,
  ImagePosition,
  Origin,
  LayoutDataBackgroundType,
  BackdropType,
  VerticalAlignmentAlignment,
  ResponsivenessBehaviour,
  DesignTarget,
  Scaling,
  ImagePositionPosition,
  CardDataBackgroundType,
  ListStyle,
  Indentation,
  SmartBlockDataType,
  ColumnSize,
  BadgeTypeType,
  RequestedFields,
  CommonSortOrder,
  SortOrder,
  Relation,
  WebhookIdentityType,
} from './events-v3-event-wix-events-v-2.universal.js';
export {
  Event,
  Location,
  Address,
  AddressStreetOneOf,
  StreetAddress,
  AddressLocation,
  Subdivision,
  DateAndTimeSettings,
  Recurrences,
  Occurrence,
  Formatted,
  TimeDuration,
  Registration,
  RsvpRegistration,
  RsvpConfirmationMessages,
  PositiveResponseConfirmation,
  NegativeResponseConfirmation,
  TicketsRegistration,
  Money,
  TaxSettings,
  TicketsConfirmationMessages,
  ExternalRegistration,
  CalendarUrls,
  Form,
  InputControl,
  Input,
  OptionSelection,
  OptionSelectionSelectedOptionOneOf,
  Label,
  FormMessages,
  RsvpFormMessages,
  RsvpFormMessagesPositiveResponseConfirmation,
  RsvpFormMessagesNegativeResponseConfirmation,
  Positive,
  Negative,
  CheckoutFormMessages,
  ResponseConfirmation,
  RegistrationClosedMessages,
  TicketsUnavailableMessages,
  Summaries,
  EventsMoney,
  Rsvps,
  Tickets,
  GuestListSettings,
  Feed,
  OnlineConferencing,
  OnlineConferencingSession,
  SeoSettings,
  SeoSchema,
  Keyword,
  Tag,
  Settings,
  AgendaSettings,
  EventCategories,
  EventCategory,
  EventDisplaySettings,
  LabellingSettings,
  RichContent,
  Node,
  NodeDataOneOf,
  NodeStyle,
  ButtonData,
  Gradient,
  Stop,
  Border,
  Colors,
  Background,
  PluginContainerData,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Spoiler,
  Height,
  Styles,
  Link,
  LinkDataOneOf,
  Rel,
  CodeBlockData,
  TextStyle,
  DividerData,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  PDFSettings,
  GalleryData,
  Media,
  Image,
  Video,
  Item,
  ItemDataOneOf,
  GalleryOptions,
  GalleryOptionsLayout,
  ItemStyle,
  Thumbnails,
  GIFData,
  GIF,
  HeadingData,
  HTMLData,
  HTMLDataDataOneOf,
  ImageData,
  StylesBorder,
  ImageDataStyles,
  LinkPreviewData,
  LinkPreviewDataStyles,
  MapData,
  MapSettings,
  ParagraphData,
  PollData,
  Permissions,
  Option,
  PollSettings,
  PollLayout,
  OptionLayout,
  BackgroundGradient,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesign,
  OptionDesign,
  Poll,
  PollDataLayout,
  Design,
  TextData,
  Decoration,
  DecorationDataOneOf,
  AnchorData,
  ColorData,
  LinkData,
  MentionData,
  FontSizeData,
  SpoilerData,
  FontFamilyData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  BookingData,
  EventData,
  ButtonStyles,
  ImageStyles,
  RibbonStyles,
  CardStyles,
  PricingData,
  VideoData,
  PlaybackOptions,
  EmbedData,
  Oembed,
  CollapsibleListData,
  TableData,
  Dimensions,
  TableCellData,
  CellStyle,
  BorderColors,
  BorderWidths,
  ListValue,
  AudioData,
  OrderedListData,
  BulletedListData,
  BlockquoteData,
  CaptionData,
  LayoutData,
  LayoutDataBackgroundImage,
  Banner,
  LayoutDataBackground,
  Backdrop,
  LayoutCellData,
  ShapeData,
  ShapeDataStyles,
  CardData,
  CardDataBackground,
  BackgroundImage,
  TocData,
  SmartBlockData,
  SmartBlockCellData,
  Metadata,
  DocumentStyle,
  TextNodeStyle,
  BadgeList,
  Badge,
  EventStarted,
  EventEnded,
  EventReminder,
  EventPublished,
  EventCloned,
  EventCanceled,
  CreateEventRequest,
  CreateEventResponse,
  CloneEventRequest,
  CloneEventResponse,
  CloneEventFromOtherSiteRequest,
  CloneEventFromOtherSiteResponse,
  UpdateEventRequest,
  UpdateEventResponse,
  PublishDraftEventRequest,
  PublishDraftEventResponse,
  CancelEventRequest,
  CancelEventResponse,
  BulkCancelEventsByFilterRequest,
  CommonQueryV2,
  CommonQueryV2PagingMethodOneOf,
  CommonSorting,
  CommonPaging,
  BulkCancelEventsByFilterResponse,
  DeleteEventRequest,
  DeleteEventResponse,
  BulkDeleteEventsByFilterRequest,
  BulkDeleteEventsByFilterResponse,
  QueryEventsRequest,
  QueryEventsResponse,
  CommonPagingMetadataV2,
  CommonCursors,
  ExportEventsRequest,
  QueryV2,
  QueryV2PagingMethodOneOf,
  Sorting,
  Paging,
  CursorPaging,
  ExportEventsResponse,
  PagingMetadataV2,
  Cursors,
  CountEventsByStatusRequest,
  CountEventsByStatusResponse,
  FacetCounts,
  ListUserEventsRequest,
  UserFilter,
  ListUserEventsResponse,
  ListEventsByCategoryRequest,
  ListEventsByCategoryResponse,
  GetEventRequest,
  GetEventResponse,
  GetEventBySlugRequest,
  GetEventBySlugResponse,
  FindEventRequest,
  FindEventRequestFindByOneOf,
  FindEventResponse,
  Empty,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  EventCanceledEnvelope,
  EventClonedEnvelope,
  EventCreatedEnvelope,
  EventDeletedEnvelope,
  EventEndedEnvelope,
  EventPublishedEnvelope,
  EventReminderEnvelope,
  EventStartedEnvelope,
  EventUpdatedEnvelope,
  CreateEventOptions,
  CloneEventOptions,
  UpdateEventOptions,
  PublishDraftEventOptions,
  CancelEventOptions,
  BulkCancelEventsByFilterOptions,
  BulkDeleteEventsByFilterOptions,
  QueryEventsOptions,
  EventsQueryResult,
  EventsQueryBuilder,
  EventQuerySpec,
  CountEventsByStatusOptions,
  ListEventsByCategoryOptions,
  GetEventOptions,
  GetEventBySlugOptions,
} from './events-v3-event-wix-events-v-2.universal.js';
export { utils } from './events-v3-event-wix-events-v-2.universal.js';
export {
  LocationTypeWithLiterals,
  SubdivisionTypeWithLiterals,
  RecurrenceStatusStatusWithLiterals,
  StatusWithLiterals,
  RegistrationTypeTypeWithLiterals,
  RegistrationStatusStatusWithLiterals,
  ResponseTypeWithLiterals,
  TaxTypeWithLiterals,
  CheckoutTypeWithLiterals,
  GuestTypeTypeWithLiterals,
  InitialRegistrationTypeTypeWithLiterals,
  InputControlTypeWithLiterals,
  ValueTypeWithLiterals,
  ConferenceTypeTypeWithLiterals,
  TypeWithLiterals,
  NodeTypeWithLiterals,
  BackgroundTypeWithLiterals,
  GradientTypeWithLiterals,
  WidthTypeWithLiterals,
  PluginContainerDataAlignmentWithLiterals,
  ButtonDataTypeWithLiterals,
  TargetWithLiterals,
  TextAlignmentWithLiterals,
  LineStyleWithLiterals,
  WidthWithLiterals,
  DividerDataAlignmentWithLiterals,
  ViewModeWithLiterals,
  LayoutTypeWithLiterals,
  OrientationWithLiterals,
  CropWithLiterals,
  ThumbnailsAlignmentWithLiterals,
  GIFTypeWithLiterals,
  SourceWithLiterals,
  StylesPositionWithLiterals,
  MapTypeWithLiterals,
  ViewRoleWithLiterals,
  VoteRoleWithLiterals,
  PollLayoutTypeWithLiterals,
  PollLayoutDirectionWithLiterals,
  PollDesignBackgroundTypeWithLiterals,
  DecorationTypeWithLiterals,
  FontTypeWithLiterals,
  PositionWithLiterals,
  AspectRatioWithLiterals,
  ResizingWithLiterals,
  PlacementWithLiterals,
  CardStylesTypeWithLiterals,
  AlignmentWithLiterals,
  LayoutWithLiterals,
  AppTypeWithLiterals,
  InitialExpandedItemsWithLiterals,
  DirectionWithLiterals,
  VerticalAlignmentWithLiterals,
  NullValueWithLiterals,
  ImageScalingScalingWithLiterals,
  ImagePositionWithLiterals,
  OriginWithLiterals,
  LayoutDataBackgroundTypeWithLiterals,
  BackdropTypeWithLiterals,
  VerticalAlignmentAlignmentWithLiterals,
  ResponsivenessBehaviourWithLiterals,
  DesignTargetWithLiterals,
  ScalingWithLiterals,
  ImagePositionPositionWithLiterals,
  CardDataBackgroundTypeWithLiterals,
  ListStyleWithLiterals,
  IndentationWithLiterals,
  SmartBlockDataTypeWithLiterals,
  ColumnSizeWithLiterals,
  BadgeTypeTypeWithLiterals,
  RequestedFieldsWithLiterals,
  CommonSortOrderWithLiterals,
  SortOrderWithLiterals,
  RelationWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CreateEventApplicationErrors,
  CreateEventValidationErrors,
  UpdateEventApplicationErrors,
  UpdateEventValidationErrors,
  PublishDraftEventApplicationErrors,
  CancelEventApplicationErrors,
  QueryEventsApplicationErrors,
  CountEventsByStatusApplicationErrors,
  CommonQueryWithEntityContext,
  EventQuery,
} from './events-v3-event-wix-events-v-2.universal.js';
